import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ProcedureRunPlanConditionREST, updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import {getDestinationPMEIId} from 'studies/sidepanel/AddProcedureRunModalUtils';
import AddPlanUnitOperationLinksModal from 'studies/typed/shared/AddPlanUnitOperationLinksModal';
import {UnitOpLinkEdge} from 'studies/typed/shared/unit-op-linking-flowchart/UnitOpLinkingFlowchartUtils';
import {UnitOperationFields} from 'studies/typed/shared/unit-op-linking-flowchart/useUnitOpLinkingState';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import runModal from 'ui-library/runModal';

import {
  LinkToExistingUnitOperationsPlanButtonQuery,
  LinkToExistingUnitOperationsPlanButtonQueryVariables,
} from './__generated/LinkToExistingUnitOperationsPlanButtonQuery';

export const LINK_TO_EXISTING_UNIT_OPERATIONS_PLAN_BUTTON_QUERY = gql`
  query LinkToExistingUnitOperationsPlanButtonQuery(
    $studyId: StrictString!
    $procedureRunPlanId: StrictString!
  ) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        procedureRunCount
      }
      ...InaccessibleInfoGraphQL
    }
    procedureRunPlan(id: $procedureRunPlanId) {
      ... on ProcedureRunPlanNode {
        id
        templateProcedureFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            nodes {
              id
              name
            }
            procedureFlowchart {
              ... on ProcedureFlowchartNode {
                id
                parent {
                  ... on ProcedureNode {
                    id
                    procedureType
                  }
                }
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            nodes {
              id
              name
            }
            procedure {
              ... on ProcedureNode {
                id
                procedureType
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureRunPlanConditions {
          id
          name
          position
          methodParametersPlans {
            templateMethodVersion {
              id
            }
            dataParameterValues {
              parameterId
              value
            }
            materialInputValues {
              materialInputId
              value
            }
            equipmentValues {
              equipmentId
              value
            }
          }
          replicates {
            replicateNumber
            sourcePmeiIdsByTemplatePmdvId {
              templatePmdvId
              sourcePmeiId
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type ProcedureRunPlanNode = ExtractByTypename<
  LinkToExistingUnitOperationsPlanButtonQuery['procedureRunPlan'],
  'ProcedureRunPlanNode'
>;
type FlowchartVersionNode = ExtractByTypename<
  NonNullable<ProcedureRunPlanNode['templateProcedureFlowchartVersion']>,
  'ProcedureFlowchartVersionNode'
>;
type ConfigVersionProxyNode = ExtractByTypename<
  NonNullable<ProcedureRunPlanNode['procedureFlowchartConfigVersionProxy']>,
  'ProcedureFlowchartConfigVersionProxyNode'
>;

interface LinkToExistingUnitOperationsPlanButtonProps {
  studyId: string;
  procedureRunPlanId: string;
  procedureRunPlanName: string;
  /** Callback when links are saved to the backend. Called after successful save to allow parent to refresh. */
  onLinksSaved: () => void;
}

/**
 * Transforms backend sourcePmeiIdsByTemplatePmdvId format to frontend UnitOpLinkEdge format.
 * @param conditions - Plan conditions with replicates containing sourcePmeiIdsByTemplatePmdvId
 * @param methods - Method nodes from the flowchart version
 * @returns Array of UnitOpLinkEdge representing the links
 */
function transformBackendLinksToEdges(
  conditions: ProcedureRunPlanNode['procedureRunPlanConditions'],
  methods: FlowchartVersionNode['nodes'] | ConfigVersionProxyNode['nodes']
): Array<UnitOpLinkEdge> {
  const edges: Array<UnitOpLinkEdge> = [];

  conditions.forEach((condition) => {
    condition.replicates.forEach((replicate) => {
      const mappings = replicate.sourcePmeiIdsByTemplatePmdvId;
      if (mappings == null || mappings.length === 0) {
        return;
      }

      // For each mapping, find the corresponding method name and create an edge
      mappings.forEach((mapping) => {
        const method = methods.find((m) => m.id === mapping.templatePmdvId);
        assert(
          method != null,
          `Expected to find method with id ${mapping.templatePmdvId} for plan link mapping`
        );

        // Create destination PMEI ID and add edge
        const destinationPMEIId = getDestinationPMEIId(
          method.name,
          condition.name,
          replicate.replicateNumber
        );
        edges.push({sourcePMEIId: mapping.sourcePmeiId, destinationPMEIId});
      });
    });
  });

  return edges;
}

/**
 * Transforms frontend UnitOpLinkEdge format to backend sourcePMEIIdsByTemplatePMDVId format.
 * @param links - Frontend links to transform
 * @param destinationPMEIs - Destination PMEIs with templateMethodId mapping
 * @param conditions - Plan conditions to update
 * @returns Conditions with updated replicateInfos containing sourcePMEIIdsByTemplatePMDVId
 */
function transformEdgesToBackendFormat(
  links: Array<UnitOpLinkEdge>,
  destinationPMEIs: Array<UnitOperationFields>,
  conditions: ProcedureRunPlanNode['procedureRunPlanConditions']
): Array<ProcedureRunPlanConditionREST> {
  // Build mapping from destination PMEI ID to its metadata
  const destPMEIMap = new Map<
    string,
    {templateMethodId: string; conditionName: string; replicateNumber: number}
  >();
  destinationPMEIs.forEach((pmei) => {
    destPMEIMap.set(pmei.pmeiId, {
      templateMethodId: pmei.templateMethodId,
      conditionName: pmei.conditionName,
      replicateNumber: pmei.replicateNumber,
    });
  });

  // Group links by condition and replicate
  const linksByConditionReplicate = new Map<string, Map<string, Array<string>>>();

  links.forEach((link) => {
    const destInfo = destPMEIMap.get(link.destinationPMEIId);
    assert(destInfo != null, `Expected to find destination PMEI info for ${link.destinationPMEIId}`);

    const key = `${destInfo.conditionName}-${destInfo.replicateNumber}`;
    let templateMap = linksByConditionReplicate.get(key);
    if (templateMap == null) {
      templateMap = new Map();
      linksByConditionReplicate.set(key, templateMap);
    }

    let sourceIds = templateMap.get(destInfo.templateMethodId);
    if (sourceIds == null) {
      sourceIds = [];
      templateMap.set(destInfo.templateMethodId, sourceIds);
    }
    sourceIds.push(link.sourcePMEIId);
  });

  // Build the conditions update payload
  return conditions.map((condition, position) => ({
    id: condition.id,
    name: condition.name,
    position,
    methodPlans: condition.methodParametersPlans.map((plan) => ({
      methodVersionId: plan.templateMethodVersion.id,
      dataParameterValues: plan.dataParameterValues.map((pv) => ({
        dataParameterVersionId: pv.parameterId,
        values: [{value: pv.value}],
      })),
      materialInputValues: plan.materialInputValues.map((mv) => ({
        materialInputVersionId: mv.materialInputId,
        values: [{value: mv.value}],
      })),
      equipmentValues: plan.equipmentValues.map((ev) => ({
        equipmentVersionId: ev.equipmentId,
        values: [{value: ev.value}],
      })),
    })),
    replicateInfos: condition.replicates.map((replicate) => {
      const key = `${condition.name}-${replicate.replicateNumber}`;
      const templateMap = linksByConditionReplicate.get(key);

      const sourcePMEIIdsByTemplatePMDVId: Record<string, Array<string>> = {};
      if (templateMap != null) {
        templateMap.forEach((sourceIds, templateId) => {
          sourcePMEIIdsByTemplatePMDVId[templateId] = sourceIds;
        });
      }

      return {
        replicateNumber: replicate.replicateNumber,
        sourcePMEIIdsByTemplatePMDVId,
      };
    }),
  }));
}

function getDestinationPMEIsFromPlan(
  methods: FlowchartVersionNode['nodes'] | ConfigVersionProxyNode['nodes'],
  conditions: ProcedureRunPlanNode['procedureRunPlanConditions'],
  procedureId: string,
  procedureType: ProcedureType
): Array<UnitOperationFields> {
  return methods.flatMap((method, methodIndex) =>
    conditions.flatMap((condition, conditionIndex) =>
      condition.replicates.map((replicate) => ({
        pmeiId: getDestinationPMEIId(method.name, condition.name, replicate.replicateNumber),
        procedureRunName: null,
        procedureRunId: procedureId,
        procedureRunDisplayId: null,
        procedureRunOrderKey: 0,
        methodName: method.name,
        templateMethodId: method.id,
        methodOrderKey: methodIndex,
        conditionName: condition.name,
        conditionOrderKey: conditionIndex,
        replicateNumber: replicate.replicateNumber,
        procedureType,
        status: null,
      }))
    )
  );
}

export default function LinkToExistingUnitOperationsPlanButton(
  props: LinkToExistingUnitOperationsPlanButtonProps
): JSX.Element {
  const {studyId, procedureRunPlanId, procedureRunPlanName, onLinksSaved} = props;

  const result = useBenchlingQuery<
    LinkToExistingUnitOperationsPlanButtonQuery,
    LinkToExistingUnitOperationsPlanButtonQueryVariables
  >(LINK_TO_EXISTING_UNIT_OPERATIONS_PLAN_BUTTON_QUERY, {
    variables: {studyId, procedureRunPlanId},
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    renderCachedDataWhenRefetching: true,
  });

  // Extract methods and plan data for link transformations
  const {destinationPMEIs, methods, plan} = useMemo(() => {
    if (result.type !== QueryResultType.SUCCESS) {
      return {destinationPMEIs: [], methods: [], plan: null};
    }
    if (isInaccessible(result.data.procedureRunPlan)) {
      return {destinationPMEIs: [], methods: [], plan: null};
    }

    const planData = result.data.procedureRunPlan;
    const flowchartVersion = planData.procedureFlowchartConfigVersionProxy;
    const legacyFlowchartVersion = planData.templateProcedureFlowchartVersion;

    if (flowchartVersion != null && !isInaccessible(flowchartVersion)) {
      const proxy = flowchartVersion;
      return {
        destinationPMEIs: getDestinationPMEIsFromPlan(
          proxy.nodes,
          planData.procedureRunPlanConditions,
          proxy.procedure.id,
          proxy.procedure.procedureType
        ),
        methods: proxy.nodes,
        plan: planData,
      };
    } else if (legacyFlowchartVersion != null && !isInaccessible(legacyFlowchartVersion)) {
      const version = legacyFlowchartVersion;
      if (version.procedureFlowchart.parent == null) {
        return {destinationPMEIs: [], methods: [], plan: null};
      }
      return {
        destinationPMEIs: getDestinationPMEIsFromPlan(
          version.nodes,
          planData.procedureRunPlanConditions,
          version.procedureFlowchart.parent.id,
          version.procedureFlowchart.parent.procedureType
        ),
        methods: version.nodes,
        plan: planData,
      };
    }
    return {destinationPMEIs: [], methods: [], plan: null};
  }, [result]);

  // Load existing links from the plan's persisted data
  const existingLinks = useMemo(() => {
    if (plan == null || methods.length === 0) {
      return [];
    }
    return transformBackendLinksToEdges(plan.procedureRunPlanConditions, methods);
  }, [plan, methods]);

  let disabled = true;
  let tooltipText: string | null = null;
  let onClickHandler: () => void = noop;

  if (result.type === QueryResultType.PENDING) {
    tooltipText = 'Loading...';
  } else if (result.type === QueryResultType.SUCCESS) {
    if (!isInaccessible(result.data.study) && !isInaccessible(result.data.procedureRunPlan)) {
      const study = result.data.study;
      const hasProcedureRuns = study.procedureRunCount > 0;
      disabled = !hasProcedureRuns;

      if (!hasProcedureRuns) {
        tooltipText = 'No existing recipe runs to link to';
      }
    } else {
      tooltipText = 'Unable to access study or procedure run plan';
    }
  } else {
    tooltipText = 'Failed to load data';
  }

  assert(procedureRunPlanId != null, 'Procedure Run Plan is not selected');
  assert(procedureRunPlanName != null, 'Procedure Run Plan is not selected');

  if (!disabled && plan != null) {
    onClickHandler = async () => {
      const newLinks = await runModal(AddPlanUnitOperationLinksModal, {
        studyId,
        procedureRunPlanName,
        destinationPMEIs,
        initialLinks: existingLinks,
      });

      // Save links to the backend if user confirmed (newLinks is not null)
      if (newLinks != null) {
        const conditionsUpdate = transformEdgesToBackendFormat(
          newLinks,
          destinationPMEIs,
          plan.procedureRunPlanConditions
        );

        await updateProcedureRunPlan({
          id: procedureRunPlanId,
          studyId,
          experimentConditions: conditionsUpdate,
        });

        // Notify parent that links were saved (for refetching if needed)
        onLinksSaved();

        // Refetch the query to get updated links (fire-and-forget, don't block the callback)
        result.refetch();
      }
    };
  }

  return (
    <Tooltip isDisabled={!disabled} text={tooltipText}>
      <Button
        dataTestAttrs={{component: 'LinkToExistingPlanButton', element: 'button'}}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MEDIUM}
        onClick={onClickHandler}
        text="Link to existing"
        icon="diagram-nested"
        disabled={disabled}
      />
    </Tooltip>
  );
}
